import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Inbox, CalendarClock, CheckCircle2, DollarSign, Images, ImagePlus,
  Newspaper, Star, Users as UsersIcon, TrendingUp, Clock, Loader2, Sparkles,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getStats, saveSettings, getSettings } from "../api";
import { toast } from "sonner";
import { T, CARD, INP } from "./theme";

function Stat({ icon: Icon, label, value, accent, delay, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: [0.2, 0.7, 0.2, 1] }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="rounded-2xl p-5 relative overflow-hidden group"
      style={CARD}
    >
      <div
        className="absolute inset-x-0 -top-px h-px opacity-70"
        style={{ background: `linear-gradient(90deg, transparent, ${accent || T.gold}, transparent)` }}
      />
      <div className="flex items-center justify-between">
        <span
          className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
          style={{ background: `${accent || T.gold}18`, color: accent || T.gold }}
        >
          <Icon size={19} />
        </span>
      </div>
      <div className="mt-4 font-display font-extrabold text-3xl tracking-tight" style={{ color: T.text }}>
        {value}
      </div>
      <div className="text-[11px] mt-1 tracking-[0.15em] uppercase font-medium" style={{ color: T.textDim }}>
        {label}
      </div>
      {sub && <div className="text-xs mt-1" style={{ color: T.textFaint }}>{sub}</div>}
    </motion.div>
  );
}

export default function DashboardHome({ user, isClient }) {
  const [s, setS] = useState(null);
  const [rev, setRev] = useState("");
  useEffect(() => {
    getStats().then((d) => { setS(d); setRev(d.monthly_revenue || ""); }).catch(() => {});
  }, []);
  const saveRevenue = async () => {
    try {
      const cur = await getSettings();
      await saveSettings({ ...cur, monthly_revenue: Number(rev) || 0 });
      toast.success("Monthly revenue updated");
    } catch { toast.error("Failed"); }
  };

  if (!s) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin" style={{ color: T.gold }} size={28} />
      </div>
    );
  }

  return (
    <div data-testid="panel-dashboard">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest mb-3"
               style={{ background: T.goldSoft, color: T.gold }}>
            <Sparkles size={11} /> {isClient ? "Client Portal" : "Overview"}
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl" style={{ color: T.text }}>
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-sm mt-1.5" style={{ color: T.textDim }}>
            {isClient ? "Manage your website content and view business insights." : "Business overview at a glance."}
          </p>
        </div>
        <div className="text-xs" style={{ color: T.textFaint }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <Stat icon={Inbox} label="Total Requests" value={s.total_requests} accent={T.gold} delay={0.02} />
        <Stat icon={TrendingUp} label="New Today" value={s.new_today} accent={T.success} delay={0.05} />
        <Stat icon={CalendarClock} label="Appointments" value={s.total_appointments} accent={T.info} delay={0.08} />
        <Stat icon={Clock} label="Pending" value={s.pending_appointments} accent={T.warning} delay={0.11} />
        <Stat icon={CheckCircle2} label="Completed" value={s.completed_jobs} accent={T.success} delay={0.14} />
        <Stat icon={DollarSign} label="Monthly Revenue" value={`$${Number(s.monthly_revenue || 0).toLocaleString()}`} accent={T.gold} delay={0.17} />
        <Stat icon={Images} label="Gallery" value={s.gallery} accent={T.purple} delay={0.20} />
        <Stat icon={ImagePlus} label="Before / After" value={s.projects} accent={T.purple} delay={0.23} />
        {!isClient && <Stat icon={Newspaper} label="Blog Posts" value={s.blog} accent={T.info} delay={0.26} />}
        <Stat icon={Star} label="Testimonials" value={s.testimonials} accent={T.gold} delay={0.29} />
        {!isClient && <Stat icon={UsersIcon} label="Team" value={s.team_members} accent={T.info} delay={0.32} />}
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="lg:col-span-2 rounded-2xl p-6" style={CARD}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold uppercase text-base tracking-wider" style={{ color: T.text }}>
              Estimate requests — last 14 days
            </h3>
            <span className="text-[10px] px-2 py-1 rounded-full font-semibold uppercase tracking-widest"
                  style={{ background: T.goldSoft, color: T.gold }}>Live</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.series} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.gold} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={T.gold} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: T.textFaint, fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: T.textFaint, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, color: T.text, fontSize: 12 }} />
              <Area type="monotone" dataKey="requests" stroke={T.gold} strokeWidth={2.5} fill="url(#gGold)" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {!isClient && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="rounded-2xl p-6" style={CARD}>
            <h3 className="font-display font-bold uppercase text-base tracking-wider mb-1" style={{ color: T.text }}>
              Monthly revenue
            </h3>
            <p className="text-xs mb-4" style={{ color: T.textDim }}>Manual entry — update anytime.</p>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: T.gold }}>$</span>
              <input data-testid="revenue-input" type="number" value={rev} onChange={(e) => setRev(e.target.value)}
                className="flex-1 px-3 py-2.5 text-sm outline-none rounded-lg" style={INP} />
            </div>
            <button data-testid="revenue-save" onClick={saveRevenue}
              className="mt-4 w-full rounded-lg py-2.5 font-semibold text-sm transition-transform active:scale-[0.98]"
              style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}>
              Save revenue
            </button>
          </motion.div>
        )}

        {isClient && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="rounded-2xl p-6" style={CARD}>
            <h3 className="font-display font-bold uppercase text-base tracking-wider mb-3" style={{ color: T.text }}>
              Quick actions
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: T.textDim }}>
              <li>• Upload new gallery photos</li>
              <li>• Add a new before/after project</li>
              <li>• Respond to new estimate requests</li>
              <li>• Confirm pending appointments</li>
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
}
